import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  DatePicker,
  TimePicker,
  Button,
  Table,
  Modal,
  Select,
  Space,
  Typography,
  Tag,
  message,
  Popconfirm,
  Row,
  Col,
  Drawer,
  Descriptions,
  Badge,
  Tooltip,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserAddOutlined,
  EyeOutlined,
  SearchOutlined,
  TeamOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  UserOutlined,
  ArrowLeftOutlined,
  FilterOutlined,
  ClearOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import './Meetingmodule.css';
import ExportMeeting from './MeetingmoduleExcel';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// Función para generar código único de reunión
const generateMeetingCode = () => {
  const prefix = 'MEET';
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}-${timestamp}-${random}`;
};

const MeetingModule = () => {
  const [meetingForm] = Form.useForm();
  const [attendeeForm] = Form.useForm();
  const [searchForm] = Form.useForm();
  
  // Estados principales
  const [meetings, setMeetings] = useState([]);
  const [filteredMeetings, setFilteredMeetings] = useState([]);
  const [currentView, setCurrentView] = useState('list'); // 'list', 'create', 'edit', 'attendees'
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [editingMeeting, setEditingMeeting] = useState(null);
  
  // Estados para asistentes
  const [attendees, setAttendees] = useState({});
  const [isAttendeeModalVisible, setIsAttendeeModalVisible] = useState(false);
  const [editingAttendee, setEditingAttendee] = useState(null);
  
  // Estado para visualización
  const [isViewDrawerVisible, setIsViewDrawerVisible] = useState(false);
  const [viewingMeeting, setViewingMeeting] = useState(null);

  // Crear o actualizar reunión
  const handleSaveMeeting = (values) => {
    const meetingData = {
      id: editingMeeting?.id || Date.now(),
      codigo: editingMeeting?.codigo || generateMeetingCode(),
      tema: values.tema,
      fecha: values.fecha.format('YYYY-MM-DD'),
      horaInicio: values.horaInicio.format('HH:mm'),
      horaFinalizacion: values.horaFinalizacion.format('HH:mm'),
      facilitador: values.facilitador,
      lugar: values.lugar,
      createdAt: editingMeeting?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    if (editingMeeting) {
      // Actualizar reunión existente
      setMeetings(meetings.map(m => m.id === editingMeeting.id ? meetingData : m));
      message.success('Reunión actualizada exitosamente');
      setCurrentView('list');
      setEditingMeeting(null);
    } else {
      // Crear nueva reunión
      setMeetings([...meetings, meetingData]);
      setFilteredMeetings([...meetings, meetingData]);
      message.success('Reunión creada exitosamente');
      setSelectedMeeting(meetingData);
      setCurrentView('attendees');
    }
    
    meetingForm.resetFields();
  };

  // Eliminar reunión
  const handleDeleteMeeting = (meeting) => {
    setMeetings(meetings.filter(m => m.id !== meeting.id));
    setFilteredMeetings(filteredMeetings.filter(m => m.id !== meeting.id));
    // Eliminar asistentes asociados
    const newAttendees = { ...attendees };
    delete newAttendees[meeting.id];
    setAttendees(newAttendees);
    message.success('Reunión eliminada exitosamente');
  };

  // Ir a editar reunión
  const handleEditMeeting = (meeting) => {
    setEditingMeeting(meeting);
    meetingForm.setFieldsValue({
      tema: meeting.tema,
      fecha: dayjs(meeting.fecha),
      horaInicio: dayjs(meeting.horaInicio, 'HH:mm'),
      horaFinalizacion: dayjs(meeting.horaFinalizacion, 'HH:mm'),
      facilitador: meeting.facilitador,
      lugar: meeting.lugar,
    });
    setCurrentView('edit');
  };

  // Ver detalles de reunión
  const handleViewMeeting = (meeting) => {
    setViewingMeeting(meeting);
    setIsViewDrawerVisible(true);
  };

  // Ir a gestión de asistentes
  const handleManageAttendees = (meeting) => {
    setSelectedMeeting(meeting);
    setCurrentView('attendees');
  };

  // Búsqueda y filtros
  const handleSearch = (values) => {
    let filtered = [...meetings];

    // Filtro por término de búsqueda
    if (values.searchTerm) {
      const term = values.searchTerm.toLowerCase();
      filtered = filtered.filter(m => 
        m.tema.toLowerCase().includes(term) ||
        m.codigo.toLowerCase().includes(term) ||
        m.facilitador.toLowerCase().includes(term) ||
        m.lugar.toLowerCase().includes(term)
      );
    }

    // Filtro por rango de fechas
    if (values.dateRange && values.dateRange.length === 2) {
      const [start, end] = values.dateRange;
      filtered = filtered.filter(m => {
        const meetingDate = dayjs(m.fecha);
        return meetingDate.isAfter(start.subtract(1, 'day')) && 
               meetingDate.isBefore(end.add(1, 'day'));
      });
    }

    // Filtro por facilitador
    if (values.facilitador) {
      filtered = filtered.filter(m => 
        m.facilitador.toLowerCase().includes(values.facilitador.toLowerCase())
      );
    }

    setFilteredMeetings(filtered);
  };

  // Limpiar filtros
  const handleClearFilters = () => {
    searchForm.resetFields();
    setFilteredMeetings(meetings);
  };

  // ==================== GESTIÓN DE ASISTENTES ====================

  const getMeetingAttendees = (meetingId) => {
    return attendees[meetingId] || [];
  };

  const showAttendeeModal = (attendee = null) => {
    if (attendee) {
      setEditingAttendee(attendee);
      attendeeForm.setFieldsValue(attendee);
    } else {
      setEditingAttendee(null);
      attendeeForm.resetFields();
    }
    setIsAttendeeModalVisible(true);
  };

  const handleSaveAttendee = (values) => {
    const meetingId = selectedMeeting.id;
    const currentAttendees = getMeetingAttendees(meetingId);

    if (editingAttendee) {
      // Editar asistente existente
      const updatedAttendees = currentAttendees.map(att => 
        att.id === editingAttendee.id ? { ...values, id: editingAttendee.id } : att
      );
      setAttendees({ ...attendees, [meetingId]: updatedAttendees });
      message.success('Asistente actualizado exitosamente');
    } else {
      // Agregar nuevo asistente
      const newAttendee = {
        ...values,
        id: Date.now()
      };
      setAttendees({
        ...attendees,
        [meetingId]: [...currentAttendees, newAttendee]
      });
      message.success('Asistente agregado exitosamente');
    }
    
    setIsAttendeeModalVisible(false);
    attendeeForm.resetFields();
    setEditingAttendee(null);
  };

  const handleDeleteAttendee = (attendeeId) => {
    const meetingId = selectedMeeting.id;
    const currentAttendees = getMeetingAttendees(meetingId);
    setAttendees({
      ...attendees,
      [meetingId]: currentAttendees.filter(att => att.id !== attendeeId)
    });
    message.success('Asistente eliminado exitosamente');
  };

  // ==================== COLUMNAS DE TABLAS ====================

  // Columnas para tabla de reuniones
  const meetingColumns = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 150,
      render: (codigo) => <Tag color="blue">{codigo}</Tag>,
    },
    {
      title: 'Tema',
      dataIndex: 'tema',
      key: 'tema',
      width: 250,
      ellipsis: true,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 120,
      render: (fecha) => dayjs(fecha).format('DD/MM/YYYY'),
      sorter: (a, b) => dayjs(a.fecha).unix() - dayjs(b.fecha).unix(),
    },
    {
      title: 'Horario',
      key: 'horario',
      width: 150,
      render: (_, record) => `${record.horaInicio} - ${record.horaFinalizacion}`,
    },
    {
      title: 'Facilitador',
      dataIndex: 'facilitador',
      key: 'facilitador',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Lugar',
      dataIndex: 'lugar',
      key: 'lugar',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Asistentes',
      key: 'attendees',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Badge count={getMeetingAttendees(record.id).length} showZero color="blue" />
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 300,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewMeeting(record)}
            />
          </Tooltip>
          <Tooltip title="Editar">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditMeeting(record)}
            />
          </Tooltip>
          <Tooltip title="Gestionar asistentes">
            <Button
              type="default"
              size="small"
              icon={<TeamOutlined />}
              onClick={() => handleManageAttendees(record)}
            />
          </Tooltip>
          <Tooltip title="Exportar a Excel">
            <ExportMeeting 
              meeting={record} 
              attendees={getMeetingAttendees(record.id)} 
            />
          </Tooltip>
          <Popconfirm
            title="¿Está seguro de eliminar esta reunión?"
            description="Esta acción eliminará también todos los asistentes."
            onConfirm={() => handleDeleteMeeting(record)}
            okText="Sí"
            cancelText="No"
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Columnas para tabla de asistentes
  const attendeeColumns = [
    {
      title: 'Nombres y Apellidos',
      dataIndex: 'nombresApellidos',
      key: 'nombresApellidos',
      width: 200,
    },
    {
      title: 'Teléfono',
      dataIndex: 'telefono',
      key: 'telefono',
      width: 130,
    },
    {
      title: 'Correo Electrónico',
      dataIndex: 'correo',
      key: 'correo',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Entidad/Dependencia',
      dataIndex: 'entidadDependencia',
      key: 'entidadDependencia',
      width: 180,
      render: (text, record) => (
        <div>
          <div>{text}</div>
          {record.tipoEntidad === 'dependencia' && record.arn && (
            <Text type="secondary" style={{ fontSize: '12px' }}>ARN: {record.arn}</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipoEntidad',
      key: 'tipoEntidad',
      width: 120,
      render: (tipo) => (
        <Tag color={tipo === 'externa' ? 'blue' : 'green'}>
          {tipo === 'externa' ? 'Externa' : 'Dependencia ARN'}
        </Tag>
      ),
    },
    {
      title: 'Planta',
      dataIndex: 'planta',
      key: 'planta',
      width: 120,
      render: (planta) => {
        const colors = {
          contratista: 'orange',
          directivo: 'purple',
          asesor: 'cyan',
          profesional: 'blue',
          tecnico: 'geekblue',
          asistencial: 'magenta'
        };
        return <Tag color={colors[planta]}>{planta.charAt(0).toUpperCase() + planta.slice(1)}</Tag>;
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => showAttendeeModal(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Está seguro de eliminar este asistente?"
            onConfirm={() => handleDeleteAttendee(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ==================== VISTAS ====================

  // Vista: Lista de reuniones
  const renderListView = () => (
    <div>
      {/* Buscadores y filtros */}
      <div className="search-panel">
        <Form form={searchForm} onFinish={handleSearch} layout="vertical">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Form.Item name="searchTerm" label="Buscar">
                <Input
                  placeholder="Código, tema, facilitador o lugar"
                  prefix={<SearchOutlined />}
                  allowClear
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="dateRange" label="Rango de Fechas">
                <RangePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder={['Fecha inicio', 'Fecha fin']}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="facilitador" label="Facilitador">
                <Input
                  placeholder="Buscar por facilitador"
                  prefix={<UserOutlined />}
                  allowClear
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>
          <div className="search-actions">
            <Space size="middle">
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<SearchOutlined />}
                size="large"
              >
                Buscar
              </Button>
              <Button 
                onClick={handleClearFilters}
                icon={<ClearOutlined />}
                size="large"
              >
                Limpiar
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCurrentView('create')}
                className="btn-success"
                size="large"
              >
                Nueva Reunión
              </Button>
            </Space>
          </div>
        </Form>
      </div>

      {/* Tabla de reuniones */}
      <Card
        className="meeting-card-enhanced card-border-accent"
        title={
          <Space size="large">
            <CalendarOutlined style={{ fontSize: '20px' }} />
            <span>Listado de Reuniones</span>
            <Badge 
              count={filteredMeetings.length} 
              showZero 
              style={{ backgroundColor: '#52c41a' }} 
            />
          </Space>
        }
      >
        <Table
          columns={meetingColumns}
          dataSource={filteredMeetings}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} reuniones`,
          }}
          scroll={{ x: 1500 }}
          locale={{ emptyText: 'No hay reuniones registradas' }}
        />
      </Card>
    </div>
  );

  // Vista: Formulario de creación/edición
  const renderFormView = () => (
    <div>
      <Button
        className="back-button-enhanced"
        onClick={() => {
          setCurrentView('list');
          setEditingMeeting(null);
          meetingForm.resetFields();
        }}
      >
        <ArrowLeftOutlined />
        <span>Volver al listado</span>
      </Button>

      <Card
        className="meeting-card-enhanced card-border-accent"
        title={
          <Space size="large">
            <CalendarOutlined style={{ fontSize: '20px' }} />
            <span>{editingMeeting ? 'Editar Reunión' : 'Nueva Reunión'}</span>
          </Space>
        }
      >
        <div className="form-container-enhanced">
          <Form
            form={meetingForm}
            layout="vertical"
            onFinish={handleSaveMeeting}
          >
            <div className="form-section-title">
              <UserOutlined />
              <span>Información General</span>
            </div>

            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Tema de la Reunión"
                  name="tema"
                  rules={[{ required: true, message: 'Por favor ingrese el tema' }]}
                >
                  <Input
                    placeholder="Ingrese el tema de la reunión"
                    prefix={<UserOutlined />}
                    size="large"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Facilitador"
                  name="facilitador"
                  rules={[{ required: true, message: 'Por favor ingrese el facilitador' }]}
                >
                  <Input
                    placeholder="Nombre del facilitador"
                    prefix={<UserOutlined />}
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <div className="form-section-title">
              <ClockCircleOutlined />
              <span>Fecha y Horario</span>
            </div>

            <Row gutter={[24, 0]}>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Fecha"
                  name="fecha"
                  rules={[{ required: true, message: 'Por favor seleccione la fecha' }]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    placeholder="Seleccione fecha"
                    size="large"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item
                  label="Hora de Inicio"
                  name="horaInicio"
                  rules={[{ required: true, message: 'Por favor seleccione la hora' }]}
                >
                  <TimePicker
                    style={{ width: '100%' }}
                    format="HH:mm"
                    placeholder="Seleccione hora"
                    size="large"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item
                  label="Hora de Finalización"
                  name="horaFinalizacion"
                  rules={[
                    { required: true, message: 'Por favor seleccione la hora' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const inicio = getFieldValue('horaInicio');
                        if (!value || !inicio || value.isAfter(inicio)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Debe ser posterior a la hora de inicio'));
                      },
                    }),
                  ]}
                >
                  <TimePicker
                    style={{ width: '100%' }}
                    format="HH:mm"
                    placeholder="Seleccione hora"
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <div className="form-section-title">
              <EnvironmentOutlined />
              <span>Ubicación</span>
            </div>

            <Form.Item
              label="Lugar de la Reunión"
              name="lugar"
              rules={[{ required: true, message: 'Por favor ingrese el lugar' }]}
            >
              <Input
                placeholder="Ingrese el lugar de la reunión"
                prefix={<EnvironmentOutlined />}
                size="large"
              />
            </Form.Item>

            <Divider />

            <Form.Item style={{ marginBottom: 0 }}>
              <Space size="large">
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  size="large"
                  className="btn-large"
                >
                  {editingMeeting ? 'Actualizar Reunión' : 'Crear Reunión'}
                </Button>
                <Button
                  onClick={() => {
                    setCurrentView('list');
                    setEditingMeeting(null);
                    meetingForm.resetFields();
                  }}
                  size="large"
                  className="btn-large"
                >
                  Cancelar
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      </Card>
    </div>
  );

  // Vista: Gestión de asistentes
  const renderAttendeesView = () => {
    const currentAttendees = getMeetingAttendees(selectedMeeting?.id);

    return (
      <div>
        {/* Botón volver mejorado */}
        <Button
          className="back-button-enhanced"
          onClick={() => {
            setCurrentView('list');
            setSelectedMeeting(null);
          }}
        >
          <ArrowLeftOutlined />
          <span>Volver al listado</span>
        </Button>

        {/* Información de la reunión con diseño mejorado */}
   <Card 
  className="meeting-info-card"
  style={{
    marginBottom: '24px',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
  }}
>
  <div style={{
    background: 'linear-gradient(135deg, #dfdfe6c4 0%, #dfdfe6c4 100%)',
    padding: '24px',
    marginTop: '-24px',
    marginLeft: '-24px',
    marginRight: '-24px',
    marginBottom: '24px'
  }}>
    <Title level={4} style={{ color: 'black', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
      <CalendarOutlined style={{ fontSize: '24px' }} />
      {selectedMeeting?.tema}
    </Title>
  </div>

  <Row gutter={[16, 16]}>
    <Col xs={24} sm={12} md={8}>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase' }}>
          Código
        </Text>
        <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
          {selectedMeeting?.codigo}
        </Tag>
      </Space>
    </Col>

    <Col xs={24} sm={12} md={8}>
      <Space direction="vertical" size={4}>
        <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase' }}>
          Fecha
        </Text>
        <Space>
          <CalendarOutlined style={{ color: '#1890ff' }} />
          <Text strong>{dayjs(selectedMeeting?.fecha).format('DD/MM/YYYY')}</Text>
        </Space>
      </Space>
    </Col>

    <Col xs={24} sm={12} md={8}>
      <Space direction="vertical" size={4}>
        <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase' }}>
          Horario
        </Text>
        <Space>
          <ClockCircleOutlined style={{ color: '#1890ff' }} />
          <Text strong>{selectedMeeting?.horaInicio} - {selectedMeeting?.horaFinalizacion}</Text>
        </Space>
      </Space>
    </Col>

    <Col xs={24} sm={12} md={8}>
      <Space direction="vertical" size={4}>
        <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase' }}>
          Lugar
        </Text>
        <Space>
          <EnvironmentOutlined style={{ color: '#1890ff' }} />
          <Text strong>{selectedMeeting?.lugar}</Text>
        </Space>
      </Space>
    </Col>

    <Col xs={24} sm={12} md={8}>
      <Space direction="vertical" size={4}>
        <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase' }}>
          Facilitador
        </Text>
        <Space>
          <UserOutlined style={{ color: '#1890ff' }} />
          <Text strong>{selectedMeeting?.facilitador}</Text>
        </Space>
      </Space>
    </Col>

    <Col xs={24} sm={12} md={8}>
      <Space direction="vertical" size={4}>
        <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase' }}>
          Total Asistentes
        </Text>
        <Space>
          <TeamOutlined style={{ color: '#52c41a' }} />
          <Badge 
            count={currentAttendees.length} 
            showZero 
            style={{ backgroundColor: '#52c41a' }}
          />
        </Space>
      </Space>
    </Col>
  </Row>
</Card>

        {/* Tabla de asistentes */}
        <Card
          className="meeting-card-enhanced card-border-accent"
          title={
            <Space size="large">
              <UserAddOutlined style={{ fontSize: '20px' }} />
              <span>Asistentes de la Reunión</span>
              <Badge 
                count={currentAttendees.length} 
                showZero 
                style={{ backgroundColor: '#52c41a' }} 
              />
            </Space>
          }
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showAttendeeModal()}
              size="large"
              className="btn-success"
            >
              Agregar Asistente
            </Button>
          }
        >
          <Table
            columns={attendeeColumns}
            dataSource={currentAttendees}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1200 }}
            locale={{ emptyText: 'No hay asistentes registrados' }}
          />
        </Card>
      </div>
    );
  };

  // ==================== MODALES Y DRAWERS ====================

  // Modal para agregar/editar asistente
  const renderAttendeeModal = () => (
    <Modal
      open={isAttendeeModalVisible}
      onCancel={() => {
        setIsAttendeeModalVisible(false);
        attendeeForm.resetFields();
        setEditingAttendee(null);
      }}
      footer={null}
      width={700}
      closable={false}
      styles={{
        body: { padding: 0 }
      }}
    >
      <div className="attendee-modal-header">
        <div className="attendee-modal-title">
          <UserAddOutlined />
          {editingAttendee ? 'Editar Asistente' : 'Agregar Asistente'}
        </div>
      </div>
      <div style={{ padding: '24px' }}>
        <Form
          form={attendeeForm}
          layout="vertical"
          onFinish={handleSaveAttendee}
        >
          <Form.Item
            label="Nombres y Apellidos"
            name="nombresApellidos"
            rules={[{ required: true, message: 'Por favor ingrese nombres y apellidos' }]}
          >
            <Input 
              placeholder="Ingrese nombres y apellidos completos" 
              size="large"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Teléfono de Contacto"
                name="telefono"
                rules={[
                  { required: true, message: 'Por favor ingrese el teléfono' },
                  { pattern: /^[0-9+\-() ]+$/, message: 'Formato de teléfono inválido' }
                ]}
              >
                <Input 
                  placeholder="Ej: +57 300 1234567" 
                  size="large"
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Correo Electrónico"
                name="correo"
                rules={[
                  { required: true, message: 'Por favor ingrese el correo' },
                  { type: 'email', message: 'Correo electrónico inválido' }
                ]}
              >
                <Input 
                  placeholder="correo@ejemplo.com" 
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Tipo de Entidad"
            name="tipoEntidad"
            rules={[{ required: true, message: 'Por favor seleccione el tipo' }]}
          >
            <Select 
              placeholder="Seleccione tipo de entidad"
              size="large"
            >
              <Option value="externa">Entidad Externa</Option>
              <Option value="dependencia">Dependencia ARN</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Entidad/Dependencia"
            name="entidadDependencia"
            rules={[{ required: true, message: 'Por favor ingrese la entidad o dependencia' }]}
          >
            <Input 
              placeholder="Nombre de la entidad o dependencia" 
              size="large"
            />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.tipoEntidad !== currentValues.tipoEntidad
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('tipoEntidad') === 'dependencia' && (
                <Form.Item
                  label="ARN"
                  name="arn"
                  rules={[{ required: true, message: 'Por favor ingrese el ARN' }]}
                >
                  <Input 
                    placeholder="Código ARN de la dependencia" 
                    size="large"
                  />
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item
            label="Tipo de Planta"
            name="planta"
            rules={[{ required: true, message: 'Por favor seleccione el tipo de planta' }]}
          >
            <Select 
              placeholder="Seleccione tipo de planta"
              size="large"
            >
              <Option value="contratista">Contratista</Option>
              <Option value="directivo">Directivo</Option>
              <Option value="asesor">Asesor</Option>
              <Option value="profesional">Profesional</Option>
              <Option value="tecnico">Técnico</Option>
              <Option value="asistencial">Asistencial</Option>
            </Select>
          </Form.Item>

          <Divider />

          <Form.Item style={{ marginBottom: 0 }}>
            <Space size="large">
              <Button 
                type="primary" 
                htmlType="submit"
                size="large"
                className="btn-large"
              >
                {editingAttendee ? 'Actualizar' : 'Agregar'}
              </Button>
              <Button 
                onClick={() => {
                  setIsAttendeeModalVisible(false);
                  attendeeForm.resetFields();
                  setEditingAttendee(null);
                }}
                size="large"
                className="btn-large"
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );

  // Drawer para visualizar reunión
  const renderViewDrawer = () => (
    <Drawer
      placement="right"
      width={600}
      onClose={() => {
        setIsViewDrawerVisible(false);
        setViewingMeeting(null);
      }}
      open={isViewDrawerVisible}
      closable={false}
      styles={{
        body: { padding: 0 }
      }}
    >
      <div className="view-drawer-header">
        <div className="view-drawer-title">
          <CalendarOutlined /> Detalles de la Reunión
        </div>
      </div>
      {viewingMeeting && (
        <div style={{ padding: '24px' }}>
          <Descriptions 
            bordered 
            column={1}
            size="middle"
          >
            <Descriptions.Item label="Código">
              <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                {viewingMeeting.codigo}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Tema">
              <strong>{viewingMeeting.tema}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Fecha">
              <Space>
                <CalendarOutlined />
                {dayjs(viewingMeeting.fecha).format('DD/MM/YYYY')}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Hora de Inicio">
              <Space>
                <ClockCircleOutlined />
                {viewingMeeting.horaInicio}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Hora de Finalización">
              <Space>
                <ClockCircleOutlined />
                {viewingMeeting.horaFinalizacion}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Facilitador">
              <Space>
                <UserOutlined />
                {viewingMeeting.facilitador}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Lugar">
              <Space>
                <EnvironmentOutlined />
                {viewingMeeting.lugar}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Total Asistentes">
              <Badge 
                count={getMeetingAttendees(viewingMeeting.id).length} 
                showZero 
                style={{ backgroundColor: '#52c41a' }}
              />
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Space size="large" style={{ width: '100%', justifyContent: 'center' }}>
            <Button
              type="primary"
              icon={<TeamOutlined />}
              onClick={() => {
                setIsViewDrawerVisible(false);
                handleManageAttendees(viewingMeeting);
              }}
              size="large"
            >
              Gestionar Asistentes
            </Button>
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                setIsViewDrawerVisible(false);
                handleEditMeeting(viewingMeeting);
              }}
              size="large"
            >
              Editar Reunión
            </Button>
          </Space>
        </div>
      )}
    </Drawer>
  );

  // ==================== RENDER PRINCIPAL ====================

  return (
    <div className="meeting-module-container">
      <div className="meeting-module-wrapper">
        {/* Encabezado principal mejorado */}
        <div className="meeting-header">
          <Title level={3} className="meeting-header-title">
            <CalendarOutlined />
            Sistema de Gestión de Reuniones
          </Title>
          <div className="meeting-header-subtitle">
            Administra reuniones, asistentes y genera reportes de manera eficiente
          </div>
        </div>

        {currentView === 'list' && renderListView()}
        {(currentView === 'create' || currentView === 'edit') && renderFormView()}
        {currentView === 'attendees' && renderAttendeesView()}

        {renderAttendeeModal()}
        {renderViewDrawer()}
      </div>
    </div>
  );
};

export default MeetingModule;